/**
 * Core OAuth popup primitive used by the login SSO flows.
 *
 * Opens the IdP authorization URL in a popup, waits for the callback page to
 * write the redirect URL into `localStorage` under the `state` key, validates
 * that the `state` round-trips, and returns the authorization code. Ember's
 * `app/utils/oauth.ts` and the Pipelines flow
 * (`features/pipelines/oauth/oauth-provider.ts`) share the storage contract but
 * wait on `popup.closed`, which loses the result whenever a provider hands the
 * flow to another window — see the note in {@link openOAuthPopup}.
 *
 * NOTE: the `/oauth2/redirect` callback page is served by the backend
 * (`customerio/services` `ui_api/ember/router.go`), not by Hydra — do not add
 * client-side handling for it.
 */
import { isEmptyString } from '@/utils/string';

const OAUTH_REQUEST_KEY = '__oauth_request';

const POLL_INTERVAL_MS = 100;
/**
 * Ceiling on the wait for the callback page, applied only to providers that
 * discard the window we opened. See {@link waitForStoredResponse}.
 *
 * A provider that hands the flow off may need longer, because this clock also
 * counts the reader finding a window that opened out of sight — `segment.ts` sets
 * its own ceiling on measured evidence, and a new handoff provider should measure
 * rather than assume this one fits.
 */
const DEFAULT_TIMEOUT_MS = 180_000;

export const NO_OAUTH_WINDOW = 'Unable to open OAuth window';
export const NO_OAUTH_RESPONSE = 'OAuth response not found';
export const OAUTH_TIMED_OUT = 'OAuth response not received in time';

export class OAuthError extends Error {
  provider: string;

  constructor(message: string, provider: string) {
    super(message);

    this.name = 'OAuthError';
    this.provider = provider;
  }
}

export type OAuthOpenResponse = {
  authorizationCode: string;
  redirectUri: string;
  provider: string;
};

export type OAuthPopupConfig = {
  /** Provider identifier, used for window naming and error attribution. */
  provider: string;
  /** IdP authorization endpoint. */
  baseUrl: string;
  clientId: string;
  scope: string;
  /** Static provider params (e.g. Google's `access_type`). */
  extraParams?: Record<string, string | number | boolean>;
  /**
   * Overrides {@link getOAuthRedirectUri}. Both this and the default resolve to the
   * same backend handler, but the value is also sent to the provider, which matches
   * it against the redirect URIs registered on its OAuth app. Set it only to keep an
   * existing registration working.
   */
  redirectUri?: string;
  width?: number;
  height?: number;
  /** Overrides the default wait for the callback page. */
  timeoutMs?: number;
  /**
   * Set when the provider may move the flow to a window we did not open, which
   * makes `popup.closed` useless as a completion signal — see
   * {@link waitForStoredResponse}. Costs responsiveness on a cancelled popup, so
   * only set it for a provider that needs it.
   */
  discardsPopupWindow?: boolean;
};

/** The redirect URI is always served by the backend at this path. */
export function getOAuthRedirectUri(): string {
  return `${window.location.origin}/oauth2/redirect`;
}

function buildAuthorizationUrl(config: OAuthPopupConfig, state: string, redirectUri: string): string {
  const url = new URL(config.baseUrl);
  const params = new URLSearchParams({
    client_id: config.clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    state,
    scope: config.scope,
  });

  for (const [key, value] of Object.entries(config.extraParams ?? {})) {
    params.set(key, String(value));
  }

  // Preserve any query already present on the authorization endpoint.
  for (const [key, value] of url.searchParams.entries()) {
    params.set(key, value);
  }

  url.search = params.toString();

  return url.toString();
}

function parseResponseUrl(responseUrl: string): Record<string, string> {
  const url = new URL(responseUrl);

  return Object.fromEntries(url.searchParams.entries());
}

/**
 * Wait for the callback page to store the redirect URL under `state`.
 *
 * Two shapes, because `popup.closed` is only sometimes a completion signal.
 *
 * Normally it is: the provider keeps the window we opened, so a closed popup
 * with nothing stored means the reader dismissed it, and failing right then is
 * the responsive thing to do.
 *
 * When {@link OAuthPopupConfig.discardsPopupWindow} is set it is not. Segment
 * redirects `id.segmentapis.com/oauth2/auth` on to `app.segment.com/authorize`,
 * and the handle we hold reports closed about 300ms in while consent carries on
 * for seconds afterwards. Reading `localStorage` at that moment finds nothing,
 * so a completed authorization was thrown away and the caller was told
 * authorization had failed. Live QA on FPS-2721 orphaned five valid codes this
 * way, each landing ~10s after the old loop gave up. Ember's
 * `app/utils/oauth.ts` polls `closed` for every provider and still has the
 * defect.
 *
 * For those providers the wait runs to `timeoutMs`, because nothing observable
 * separates "handed off, still consenting" from "closed and walked away" — the
 * window is closed either way, and discarding a real authorization is the worse
 * outcome. Providers that keep their window are unaffected and still fail the
 * moment the popup closes.
 */
async function waitForStoredResponse(state: string, popup: Window, config: OAuthPopupConfig): Promise<string | null> {
  const discardsWindow = config.discardsPopupWindow === true;
  // Only bound the wait where `popup.closed` cannot end it. A provider that keeps
  // its window is bounded by the reader closing it, as it was before this helper
  // grew a timeout — capping those at three minutes would fail a slow enterprise
  // sign-in (MFA push, password reset, creating an account mid-signup) mid-flight
  // and discard the code that arrived just after, which is the very failure this
  // helper exists to prevent. An explicit `timeoutMs` still applies to anyone.
  const timeoutMs = config.timeoutMs ?? (discardsWindow ? DEFAULT_TIMEOUT_MS : null);
  const deadline = timeoutMs == null ? Number.POSITIVE_INFINITY : Date.now() + timeoutMs;
  let responseUrl = localStorage.getItem(state);

  while (responseUrl == null && Date.now() < deadline) {
    // A closed popup is only conclusive for providers that keep the window they
    // were given. Read once more first: the callback page writes before it closes.
    if (popup.closed && !discardsWindow) {
      return localStorage.getItem(state);
    }

    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    responseUrl = localStorage.getItem(state);
  }

  return responseUrl;
}

/**
 * Release this flow's `localStorage` entries.
 *
 * `OAUTH_REQUEST_KEY` is shared with `saml-popup.ts` and the Pipelines provider,
 * and a wait that outlives its popup can overlap a newer flow — cancel then
 * retry is the ordinary path. Only clear it while it still holds this `state`,
 * so a stale wait cannot delete a live flow's value. The callback page reads and
 * clears the key itself; this only covers a flow that never reached it.
 */
function releaseRequest(state: string): void {
  localStorage.removeItem(state);

  if (localStorage.getItem(OAUTH_REQUEST_KEY) === state) {
    localStorage.removeItem(OAUTH_REQUEST_KEY);
  }
}

/** Validate the callback params and pull out the authorization code. */
function readAuthorizationCode(responseUrl: string, state: string, provider: string): string {
  const response = parseResponseUrl(responseUrl);

  if (response.state !== state) {
    throw new OAuthError("OAuth state response didn't match. Discarding.", provider);
  }

  // The IdP can redirect back with an error instead of a code (e.g. the user
  // denied consent) — surface it rather than reporting a missing code.
  // https://datatracker.ietf.org/doc/html/rfc6749#section-4.1.2.1
  if (!isEmptyString(response.error) || !isEmptyString(response.error_description)) {
    const detail = !isEmptyString(response.error_description) ? response.error_description : response.error;
    throw new OAuthError(`OAuth error: ${detail}`, provider);
  }

  if (response.code == null) {
    throw new OAuthError('OAuth authorization code not found', provider);
  }

  return response.code;
}

/**
 * Open the OAuth popup and resolve with the authorization code once the callback
 * page hands one back. Rejects with {@link OAuthError} on every failure mode
 * (popup blocked, no stored response, wait timed out, state mismatch, missing
 * code) so a resolved response always has its fields populated.
 */
export async function openOAuthPopup(config: OAuthPopupConfig): Promise<OAuthOpenResponse> {
  const state = window.crypto.randomUUID();
  const redirectUri = config.redirectUri ?? getOAuthRedirectUri();

  localStorage.setItem(OAUTH_REQUEST_KEY, state);

  const width = config.width ?? 500;
  const height = config.height ?? 500;
  const popup = window.open(
    buildAuthorizationUrl(config, state, redirectUri),
    '_blank',
    `width=${width},height=${height},left=${screen.width / 2 - width / 2},top=${screen.height / 2 - height / 2}`,
  );

  if (popup == null) {
    throw new OAuthError(NO_OAUTH_WINDOW, config.provider);
  }

  const responseUrl = await waitForStoredResponse(state, popup, config);
  releaseRequest(state);

  if (responseUrl == null) {
    // For a provider that keeps its window, a closed popup with nothing stored is an
    // abandoned or failed attempt, and one still open means the wait ran out while
    // the reader was mid-consent. For a provider that discards the window, `closed`
    // says nothing either way — it closes on the handoff, seconds before consent
    // finishes — so the only thing that can have ended the wait is the ceiling.
    const abandonedPopup = popup.closed && config.discardsPopupWindow !== true;
    throw new OAuthError(abandonedPopup ? NO_OAUTH_RESPONSE : OAUTH_TIMED_OUT, config.provider);
  }

  return {
    authorizationCode: readAuthorizationCode(responseUrl, state, config.provider),
    redirectUri,
    provider: config.provider,
  };
}
